"use client"

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import { 
  Bell, Check, X, AlertCircle, Info, CheckCircle, Package, Heart, 
  User, Tag, CreditCard, ShoppingBag, Trash2, Filter, RefreshCw 
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useNotifications } from '@/contexts/notification-context'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'

interface NotificationItem {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  category: 'order' | 'product' | 'account' | 'system' | 'promotion'
  icon: string
  title: string
  message: string
  timestamp: Date | string
  read: boolean
  actionUrl?: string
  actionText?: string
  orderId?: string
  order?: {
    orderNumber: string
    status: string
    total: number
  }
  metadata?: {
    recipient: string
    type: string
  }
}

export function NotificationBell() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('all')
  const { showInfo, showError } = useNotifications()
  const { user } = useAuth()

  const fetchNotifications = async (category?: string) => {
    // Don't fetch notifications if user is not authenticated
    if (!user) {
      setNotifications([])
      setUnreadCount(0)
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (category && category !== 'all') {
        params.append('category', category)
      }
      
      const response = await fetch(`/api/notifications?${params.toString()}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setNotifications(data.notifications || [])
        setUnreadCount(data.unreadCount || 0)
      } else {
        const errorData = await response.text()
        console.error('[NotificationBell] API error:', response.status, errorData)
        throw new Error(`Failed to fetch notifications: ${response.status}`)
      }
    } catch (error) {
      console.error('Error fetching notifications:', error)
      // Silently fall back to demo data without showing error alert
      
      // Fallback to demo data if API fails
      const demoNotifications: NotificationItem[] = [
        {
          id: '1',
          type: 'success',
          category: 'order',
          icon: 'package',
          title: 'Order Confirmed',
          message: 'Your order #12345 has been confirmed and is being processed.',
          timestamp: new Date(Date.now() - 1000 * 60 * 30),
          read: false,
          actionUrl: '/account/orders/12345',
          actionText: 'View Order',
          orderId: '12345',
          order: {
            orderNumber: '#12345',
            status: 'PROCESSING',
            total: 2500
          }
        },
        {
          id: '2',
          type: 'info',
          category: 'product',
          icon: 'heart',
          title: 'Wishlist Item Back in Stock',
          message: 'The "Summer Dress" you wishlisted is now available!',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
          read: false,
          actionUrl: '/account/wishlist',
          actionText: 'View Wishlist'
        },
        {
          id: '3',
          type: 'success',
          category: 'promotion',
          icon: 'tag',
          title: 'Flash Sale Started!',
          message: 'Get up to 50% off on selected items. Limited time offer!',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4),
          read: true,
          actionUrl: '/shop',
          actionText: 'Shop Sale'
        },
        {
          id: '4',
          type: 'warning',
          category: 'account',
          icon: 'user',
          title: 'Profile Incomplete',
          message: 'Complete your profile to get personalized recommendations.',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
          read: true,
          actionUrl: '/account',
          actionText: 'Complete Profile'
        }
      ]
      setNotifications(demoNotifications)
      setUnreadCount(demoNotifications.filter(n => !n.read).length)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      fetchNotifications()
      
      // Refresh notifications every 30 seconds
      const interval = setInterval(() => fetchNotifications(), 30000)
      
      return () => clearInterval(interval)
    } else {
      // Clear notifications when user is not authenticated
      setNotifications([])
      setUnreadCount(0)
    }
  }, [user])

  const markAsRead = async (id: string) => {
    if (!user) {
      return
    }

    try {
      const response = await fetch('/api/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'mark-read', notificationId: id })
      })

      if (response.ok) {
        setNotifications(prev =>
          prev.map(n =>
            n.id === id ? { ...n, read: true } : n
          )
        )
        setUnreadCount(prev => Math.max(0, prev - 1))
      } else {
        console.error('Failed to mark notification as read:', response.status)
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    if (!user) {
      return
    }

    try {
      const response = await fetch('/api/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'mark-all-read' })
      })

      if (response.ok) {
        setNotifications(prev =>
          prev.map(n => ({ ...n, read: true }))
        )
        setUnreadCount(0)
        showInfo('All notifications marked as read')
      } else {
        console.error('Failed to mark all notifications as read:', response.status)
        showError('Failed to mark notifications as read')
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      showError('Failed to mark notifications as read')
    }
  }

  const deleteNotification = async (id: string) => {
    if (!user) {
      return
    }

    try {
      const response = await fetch('/api/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'delete', notificationId: id })
      })

      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.id !== id))
        setUnreadCount(prev => {
          const notification = notifications.find(n => n.id === id)
          return notification && !notification.read ? Math.max(0, prev - 1) : prev
        })
        showInfo('Notification deleted')
      } else {
        console.error('Failed to delete notification:', response.status)
        showError('Failed to delete notification')
      }
    } catch (error) {
      console.error('Error deleting notification:', error)
      showError('Failed to delete notification')
    }
  }

  const getIcon = (iconName: string, type: NotificationItem['type']) => {
    const iconClass = cn(
      "h-4 w-4",
      type === 'success' && "text-green-500",
      type === 'error' && "text-red-500",
      type === 'warning' && "text-yellow-500",
      type === 'info' && "text-blue-500"
    )

    switch (iconName) {
      case 'package':
        return <Package className={iconClass} />
      case 'heart':
        return <Heart className={iconClass} />
      case 'user':
        return <User className={iconClass} />
      case 'tag':
        return <Tag className={iconClass} />
      case 'credit-card':
        return <CreditCard className={iconClass} />
      case 'shopping-bag':
        return <ShoppingBag className={iconClass} />
      default:
        return type === 'success' ? <CheckCircle className={iconClass} /> :
               type === 'error' ? <X className={iconClass} /> :
               type === 'warning' ? <AlertCircle className={iconClass} /> :
               <Info className={iconClass} />
    }
  }

  const formatTime = (timestamp: Date | string) => {
    const now = new Date()
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    return `${days}d ago`
  }

  const filterNotifications = (category: string) => {
    if (category === 'all') return notifications
    return notifications.filter(n => n.category === category)
  }

  const getTabCount = (category: string) => {
    const filtered = filterNotifications(category)
    return filtered.filter(n => !n.read).length
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    if (user) {
      fetchNotifications(value === 'all' ? undefined : value)
    }
  }

  const currentNotifications = filterNotifications(activeTab)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Notifications"
          disabled={!user}
        >
          <Bell className={cn("h-5 w-5", !user && "text-muted-foreground")} />
          {user && unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="font-semibold">Notifications</h4>
          {user && (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchNotifications(activeTab === 'all' ? undefined : activeTab)}
                disabled={loading}
              >
                <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                disabled={unreadCount === 0 || loading}
              >
                Mark all read
              </Button>
            </div>
          )}
        </div>

        {user ? (
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full grid-cols-6 px-2 py-1">
              <TabsTrigger value="all" className="text-xs px-2">
                All
                {getTabCount('all') > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-xs">
                    {getTabCount('all')}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="order" className="text-xs px-1">
                <Package className="h-3 w-3" />
                {getTabCount('order') > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-xs">
                    {getTabCount('order')}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="product" className="text-xs px-1">
                <Heart className="h-3 w-3" />
                {getTabCount('product') > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-xs">
                    {getTabCount('product')}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="account" className="text-xs px-1">
                <User className="h-3 w-3" />
                {getTabCount('account') > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-xs">
                    {getTabCount('account')}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="promotion" className="text-xs px-1">
                <Tag className="h-3 w-3" />
                {getTabCount('promotion') > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-xs">
                    {getTabCount('promotion')}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="system" className="text-xs px-1">
                <Bell className="h-3 w-3" />
                {getTabCount('system') > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-xs">
                    {getTabCount('system')}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

          <TabsContent value={activeTab} className="m-0">
            <ScrollArea className="h-80">
              {currentNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <Bell className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground">No notifications</p>
                  <p className="text-xs text-muted-foreground">
                    {activeTab === 'all' ? "You're all caught up!" : `No ${activeTab} notifications`}
                  </p>
                </div>
              ) : (
                <div className="p-2">
                  {currentNotifications.map((notification, index) => (
                    <div key={notification.id}>
                      <div
                        className={cn(
                          "group relative flex items-start gap-3 p-3 rounded-lg transition-colors",
                          !notification.read && "bg-muted/50"
                        )}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {getIcon(notification.icon, notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <p className="text-sm font-medium leading-none">
                              {notification.title}
                            </p>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {!notification.read && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => markAsRead(notification.id)}
                                  title="Mark as read"
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                              )}
                              {user && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                  onClick={() => deleteNotification(notification.id)}
                                  title="Delete notification"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                            {notification.message}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-muted-foreground">
                              {formatTime(notification.timestamp)}
                            </span>
                            {notification.actionUrl && notification.actionText && (
                              <Link href={notification.actionUrl} onClick={() => setIsOpen(false)}>
                                <Button variant="outline" size="sm" className="h-6 px-2 text-xs">
                                  {notification.actionText}
                                </Button>
                              </Link>
                            )}
                          </div>
                          {notification.order && (
                            <div className="mt-2 p-2 bg-muted/30 rounded text-xs">
                              <div className="flex justify-between">
                                <span>Order: {notification.order.orderNumber}</span>
                                <span className="font-medium">₹{Number(notification.order.total) || 0}</span>
                              </div>
                              <div className="text-muted-foreground">
                                Status: {notification.order.status}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {index < currentNotifications.length - 1 && <Separator className="my-1" />}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
        ) : (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <Bell className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">Please log in to view notifications</p>
            <p className="text-xs text-muted-foreground">
              Sign in to see your personalized notifications
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
} 